import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import SearchableDropdown from './SearchableDropdown';
import QuickAddModal from './QuickAddModal_Dropdown';
import '../styles/nassar-theme.css';
import './AddClearanceFileModal.css';

const EditShipmentModal = ({ isOpen, onClose, onSuccess, shipmentData }) => {
    const [formData, setFormData] = useState({
        job: '',
        clearance_file_num: '',
        status: '',
        customer: '',
        supplier: '',
        quantity: '',
        shipment_type: '',
        container_num: '',
        shipment_term: '',
        free_period_allowed: '',
        origin_country: '',
        expected_arrival_date: '',
        shipment_cost: '',
        total_cost: '',
        selling_price: '',
        commission: '',
        note: ''
    });

    // Custom date inputs state
    const [arrivalDate, setArrivalDate] = useState({ day: '', month: '', year: '' });

    const [lookupData, setLookupData] = useState({
        statuses: [],
        customers: [],
        suppliers: [],
        shipment_types: [],
        shipment_terms: [],
        origin_countries: []
    });

    const [quickAddModal, setQuickAddModal] = useState({
        isOpen: false,
        type: '',
        title: '',
        fieldLabel: '',
        endpoint: '',
        fields: null,
        editMode: false,
        editId: null
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        item: null,
        endpoint: '',
        hasError: false
    });

    useEffect(() => {
        if (isOpen) {
            fetchLookupData();
        }
    }, [isOpen]);

    // Pre-fill form with shipmentData when modal opens
    useEffect(() => {
        if (isOpen && shipmentData) {
            setFormData({
                job: shipmentData.job || '',
                clearance_file_num: shipmentData.clearance_file_num || '',
                status: shipmentData.status || '',
                customer: shipmentData.customer || '',
                supplier: shipmentData.supplier || '',
                quantity: shipmentData.quantity || '',
                shipment_type: shipmentData.shipment_type || '',
                container_num: shipmentData.container_num || '',
                shipment_term: shipmentData.shipment_term || '',
                free_period_allowed: shipmentData.free_period_allowed || '',
                origin_country: shipmentData.origin_country || '',
                expected_arrival_date: shipmentData.expected_arrival_date || '',
                shipment_cost: shipmentData.shipment_cost || '',
                total_cost: shipmentData.total_cost || '',
                selling_price: shipmentData.selling_price || '',
                commission: shipmentData.commission || '',
                note: shipmentData.note || ''
            });

            // Parse arrival date
            if (shipmentData.expected_arrival_date) {
                const [year, month, day] = shipmentData.expected_arrival_date.split('-');
                setArrivalDate({ day: day || '', month: month || '', year: year || '' });
            } else {
                setArrivalDate({ day: '', month: '', year: '' });
            }
        }
    }, [isOpen, shipmentData]);

    const fetchLookupData = async () => {
        try {
            const [statuses, customers, suppliers, shipmentTypes, shipmentTerms, originCountries] = await Promise.all([
                axiosInstance.get('/statuses/'),
                axiosInstance.get('/customers-st/'),
                axiosInstance.get('/suppliers/'),
                axiosInstance.get('/shipment-types-st/'),
                axiosInstance.get('/shipment-terms-st/'),
                axiosInstance.get('/origin-countries-st/')
            ]);

            const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '', 'ar');

            setLookupData({
                statuses: statuses.data.sort(sortByName),
                customers: customers.data.sort(sortByName),
                suppliers: suppliers.data.sort(sortByName),
                shipment_types: shipmentTypes.data.sort(sortByName),
                shipment_terms: shipmentTerms.data.sort(sortByName),
                origin_countries: originCountries.data.sort(sortByName)
            });
        } catch (err) {
            console.error('Error fetching lookup data:', err);
        }
    };

    // Calculate total_cost and commission
    const calculateDerivedValues = (data) => {
        const quantity = parseFloat(data.quantity) || 0;
        const shipmentCost = parseFloat(data.shipment_cost) || 0;
        const sellingPrice = parseFloat(data.selling_price) || 0;

        // المجموع = الكمية * تكلفة الشحن
        const totalCost = quantity * shipmentCost;
        // العمولة = سعر البيع - تكلفة الشحن
        const commission = sellingPrice - shipmentCost;

        return {
            total_cost: totalCost > 0 ? totalCost.toFixed(2) : '',
            commission: (sellingPrice > 0 || shipmentCost > 0) ? commission.toFixed(2) : ''
        };
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Update the field value
        const newFormData = { ...formData, [name]: value };
        
        // Recalculate derived values if relevant fields change
        if (['quantity', 'shipment_cost', 'selling_price'].includes(name)) {
            const derived = calculateDerivedValues(newFormData);
            newFormData.total_cost = derived.total_cost;
            newFormData.commission = derived.commission;
        }
        
        setFormData(newFormData);
        if (error) setError('');
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const handleDropdownChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    // Custom date handling functions
    const handleDateChange = (field, value) => {
        // Allow only numbers
        const numericValue = value.replace(/\D/g, '');
        
        let maxLength = field === 'year' ? 4 : 2;
        let maxValue = field === 'day' ? 31 : field === 'month' ? 12 : 9999;
        let truncatedValue = numericValue.slice(0, maxLength);
        
        // Validate max values
        if (truncatedValue && parseInt(truncatedValue) > maxValue) {
            truncatedValue = String(maxValue);
        }
        
        const newDate = { ...arrivalDate, [field]: truncatedValue };
        setArrivalDate(newDate);
        updateFormDataDate(newDate);
        
        // Clear validation error
        if (validationErrors.expected_arrival_date) {
            setValidationErrors(prev => ({ ...prev, expected_arrival_date: false }));
        }
        if (error) setError('');
    };

    const updateFormDataDate = (dateObj) => {
        if (dateObj.day && dateObj.month && dateObj.year && dateObj.year.length === 4) {
            const formattedDate = `${dateObj.year}-${dateObj.month.padStart(2, '0')}-${dateObj.day.padStart(2, '0')}`;
            setFormData(prev => ({ ...prev, expected_arrival_date: formattedDate }));
        } else {
            setFormData(prev => ({ ...prev, expected_arrival_date: '' }));
        }
    };

    const handleDatePickerChange = (e) => {
        const value = e.target.value;
        if (value) {
            const [year, month, day] = value.split('-');
            setArrivalDate({ day, month, year });
            setFormData(prev => ({ ...prev, expected_arrival_date: value }));
        }
        if (error) setError('');
    };

    const openQuickAdd = (type, title, fieldLabel, endpoint, fields = null) => {
        setQuickAddModal({
            isOpen: true,
            type,
            title,
            fieldLabel,
            endpoint,
            fields,
            editMode: false,
            editId: null
        });
    };

    const handleEdit = (type, title, fieldLabel, endpoint, fields, item) => {
        setQuickAddModal({
            isOpen: true,
            type,
            title: `تعديل ${title.replace('إضافة ', '').replace(' جديدة', '').replace(' جديد', '').replace('جديدة', '').replace('جديد', '').trim()}`,
            fieldLabel,
            endpoint,
            fields,
            editMode: true,
            editId: item.id,
            editData: item
        });
    };

    const handleDeleteLookup = (endpoint, item) => {
        setDeleteModal({
            isOpen: true,
            item,
            endpoint,
            hasError: false
        });
    };

    const confirmDeleteItem = async () => {
        const { endpoint, item } = deleteModal;
        try {
            await axiosInstance.delete(`${endpoint}${item.id}/`);
            await fetchLookupData();
            closeDeleteModal();
        } catch (err) {
            const errorData = err.response?.data;
            if (errorData?.error) {
                setError(errorData.error);
                setDeleteModal(prev => ({ ...prev, hasError: true }));
            } else {
                setError('حدث خطأ أثناء الحذف');
                setDeleteModal(prev => ({ ...prev, hasError: true }));
            }
        }
    };

    const closeDeleteModal = () => {
        setDeleteModal({ isOpen: false, item: null, endpoint: '', hasError: false });
        setError('');
    };

    const handleQuickAddSave = async (data) => {
        const { endpoint, editMode, editId } = quickAddModal;
        
        let response;
        if (editMode && editId) {
            response = await axiosInstance.put(`${endpoint}${editId}/`, data);
        } else {
            response = await axiosInstance.post(endpoint, data);
        }
        
        await fetchLookupData();
        
        const fieldMap = {
            '/statuses/': 'status',
            '/customers-st/': 'customer',
            '/suppliers/': 'supplier',
            '/shipment-types-st/': 'shipment_type',
            '/shipment-terms-st/': 'shipment_term',
            '/origin-countries-st/': 'origin_country'
        };
        
        const fieldName = fieldMap[endpoint];
        if (fieldName && response.data.id) {
            setFormData(prev => ({ ...prev, [fieldName]: response.data.id }));
        }
        
        setQuickAddModal({ ...quickAddModal, isOpen: false });
    };

    const validateForm = () => {
        // These fields are required
        const requiredFields = [];

        const errors = {};
        let hasErrors = false;

        for (const field of requiredFields) {
            if (!formData[field] || formData[field] === '') {
                errors[field] = true;
                hasErrors = true;
            }
        }

        if (hasErrors) {
            setValidationErrors(errors);
            setError('الرجاء ملء جميع الحقول المطلوبة');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsLoading(true);
        setError('');

        try {
            const submitData = {
                job: formData.job || null,
                clearance_file_num: formData.clearance_file_num || null,
                status: formData.status || null,
                customer: formData.customer || null,
                supplier: formData.supplier || null,
                quantity: formData.quantity ? parseInt(formData.quantity) : null,
                shipment_type: formData.shipment_type || null,
                container_num: formData.container_num || null,
                shipment_term: formData.shipment_term || null,
                free_period_allowed: formData.free_period_allowed || null,
                origin_country: formData.origin_country || null,
                expected_arrival_date: formData.expected_arrival_date || null,
                shipment_cost: formData.shipment_cost ? parseFloat(formData.shipment_cost) : null,
                total_cost: formData.total_cost ? parseFloat(formData.total_cost) : null,
                selling_price: formData.selling_price ? parseFloat(formData.selling_price) : null,
                commission: formData.commission ? parseFloat(formData.commission) : null,
                note: formData.note || null
            };

            await axiosInstance.put(`/shipment-tracking/${shipmentData.id}/`, submitData);
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Error updating shipment:', err);
            setError(err.response?.data?.message || 'حدث خطأ أثناء تحديث الشحنة');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }

        setIsDeleting(true);
        setError('');

        try {
            await axiosInstance.delete(`/shipment-tracking/${shipmentData.id}/`);
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Error deleting shipment:', err);
            setError(err.response?.data?.message || 'حدث خطأ أثناء حذف الشحنة');
        } finally {
            setIsDeleting(false);
            setConfirmDelete(false);
        }
    };

    const handleClose = () => {
        setFormData({
            job: '',
            clearance_file_num: '',
            status: '',
            customer: '',
            supplier: '',
            quantity: '',
            shipment_type: '',
            container_num: '',
            shipment_term: '',
            free_period_allowed: '',
            origin_country: '',
            expected_arrival_date: '',
            shipment_cost: '',
            total_cost: '',
            selling_price: '',
            commission: '',
            note: ''
        });
        setArrivalDate({ day: '', month: '', year: '' });
        setError('');
        setValidationErrors({});
        setConfirmDelete(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal modal-large add-file-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3 className="modal-title">تعديل الشحنة - {shipmentData?.job || 'بدون Job'}</h3>
                        <button type="button" className="modal-close" onClick={handleClose}>
                            <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {/* Section 1: معلومات أساسية */}
                            <div className="form-section">
                                <h4 className="section-title">معلومات أساسية</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Job
                                        </label>
                                        <input
                                            type="text"
                                            name="job"
                                            className="form-input"
                                            value={formData.job}
                                            onChange={handleInputChange}
                                            placeholder="أدخل رقم الـ Job"
                                            style={{
                                                borderColor: validationErrors.job ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.job ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            رقم ملف التخليص
                                        </label>
                                        <input
                                            type="text"
                                            name="clearance_file_num"
                                            className="form-input"
                                            value={formData.clearance_file_num}
                                            onChange={handleInputChange}
                                            placeholder="أدخل رقم ملف التخليص"
                                            style={{
                                                borderColor: validationErrors.clearance_file_num ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.clearance_file_num ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <SearchableDropdown
                                        label="الحالة"
                                        options={lookupData.statuses}
                                        value={formData.status}
                                        onChange={(val) => handleDropdownChange('status', val)}
                                        onAddNew={() => openQuickAdd('status', 'إضافة حالة جديدة', 'اسم الحالة', '/statuses/')}
                                        onEdit={(item) => handleEdit('status', 'إضافة حالة جديدة', 'اسم الحالة', '/statuses/', null, item)}
                                        onDelete={(item) => handleDeleteLookup('/statuses/', item)}
                                        addNewLabel="إضافة حالة جديدة"
                                        hasError={validationErrors.status}
                                    />

                                    <SearchableDropdown
                                        label="اسم الزبون"
                                        options={lookupData.customers}
                                        value={formData.customer}
                                        onChange={(val) => handleDropdownChange('customer', val)}
                                        onAddNew={() => openQuickAdd('customer', 'إضافة زبون جديد', 'اسم الزبون', '/customers-st/')}
                                        onEdit={(item) => handleEdit('customer', 'إضافة زبون جديد', 'اسم الزبون', '/customers-st/', null, item)}
                                        onDelete={(item) => handleDeleteLookup('/customers-st/', item)}
                                        addNewLabel="إضافة زبون جديد"
                                        hasError={validationErrors.customer}
                                    />
                                </div>
                            </div>

                            {/* Section 2: معلومات المورد والشحن */}
                            <div className="form-section">
                                <h4 className="section-title">معلومات المورد والشحن</h4>
                                <div className="form-grid">
                                    <SearchableDropdown
                                        label="اسم المورد"
                                        options={lookupData.suppliers}
                                        value={formData.supplier}
                                        onChange={(val) => handleDropdownChange('supplier', val)}
                                        onAddNew={() => openQuickAdd('supplier', 'إضافة مورد جديد', 'اسم المورد', '/suppliers/')}
                                        onEdit={(item) => handleEdit('supplier', 'إضافة مورد جديد', 'اسم المورد', '/suppliers/', null, item)}
                                        onDelete={(item) => handleDeleteLookup('/suppliers/', item)}
                                        addNewLabel="إضافة مورد جديد"
                                        hasError={validationErrors.supplier}
                                    />

                                    <div className="form-group">
                                        <label className="form-label">
                                            الكمية
                                        </label>
                                        <input
                                            type="number"
                                            name="quantity"
                                            className="form-input"
                                            value={formData.quantity}
                                            onChange={handleInputChange}
                                            placeholder="أدخل الكمية (اختياري)"
                                            min="1"
                                            style={{
                                                borderColor: validationErrors.quantity ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.quantity ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <SearchableDropdown
                                        label="نوع الشحنة"
                                        options={lookupData.shipment_types}
                                        value={formData.shipment_type}
                                        onChange={(val) => handleDropdownChange('shipment_type', val)}
                                        onAddNew={() => openQuickAdd('shipment_type', 'إضافة نوع شحنة جديد', 'نوع الشحنة', '/shipment-types-st/')}
                                        onEdit={(item) => handleEdit('shipment_type', 'إضافة نوع شحنة جديد', 'نوع الشحنة', '/shipment-types-st/', null, item)}
                                        onDelete={(item) => handleDeleteLookup('/shipment-types-st/', item)}
                                        addNewLabel="إضافة نوع جديد"
                                        hasError={validationErrors.shipment_type}
                                    />

                                    <div className="form-group">
                                        <label className="form-label">
                                            رقم الحاوية
                                        </label>
                                        <input
                                            type="text"
                                            name="container_num"
                                            className="form-input"
                                            value={formData.container_num}
                                            onChange={handleInputChange}
                                            placeholder="أدخل رقم الحاوية (اختياري)"
                                            style={{
                                                borderColor: validationErrors.container_num ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.container_num ? '2px' : undefined
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: شروط الشحن والمنشأ */}
                            <div className="form-section">
                                <h4 className="section-title">شروط الشحن والمنشأ</h4>
                                <div className="form-grid">
                                    <SearchableDropdown
                                        label="شروط الشحن"
                                        options={lookupData.shipment_terms}
                                        value={formData.shipment_term}
                                        onChange={(val) => handleDropdownChange('shipment_term', val)}
                                        onAddNew={() => openQuickAdd('shipment_term', 'إضافة شرط شحن جديد', 'شرط الشحن', '/shipment-terms-st/')}
                                        onEdit={(item) => handleEdit('shipment_term', 'إضافة شرط شحن جديد', 'شرط الشحن', '/shipment-terms-st/', null, item)}
                                        onDelete={(item) => handleDeleteLookup('/shipment-terms-st/', item)}
                                        addNewLabel="إضافة شرط جديد"
                                        hasError={validationErrors.shipment_term}
                                    />

                                    <div className="form-group">
                                        <label className="form-label">
                                            الفترة المجانية المسموح بها
                                        </label>
                                        <input
                                            type="text"
                                            name="free_period_allowed"
                                            className="form-input"
                                            value={formData.free_period_allowed}
                                            onChange={handleInputChange}
                                            placeholder="أدخل الفترة المجانية المسموح بها (اختياري)"
                                            style={{
                                                borderColor: validationErrors.free_period_allowed ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.free_period_allowed ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <SearchableDropdown
                                        label="بلد المنشأ"
                                        options={lookupData.origin_countries}
                                        value={formData.origin_country}
                                        onChange={(val) => handleDropdownChange('origin_country', val)}
                                        onAddNew={() => openQuickAdd('origin_country', 'إضافة بلد منشأ جديد', 'اسم البلد', '/origin-countries-st/')}
                                        onEdit={(item) => handleEdit('origin_country', 'إضافة بلد منشأ جديد', 'اسم البلد', '/origin-countries-st/', null, item)}
                                        onDelete={(item) => handleDeleteLookup('/origin-countries-st/', item)}
                                        addNewLabel="إضافة بلد جديد"
                                        hasError={validationErrors.origin_country}
                                    />

                                    {/* تاريخ الوصول المتوقع */}
                                    <div className="form-group">
                                        <label className="form-label">
                                            تاريخ الوصول المتوقع
                                        </label>
                                        <div className={`custom-date-input ${validationErrors.expected_arrival_date ? 'has-error' : ''}`}>
                                            <div className="date-display">
                                                <div className="date-field day-field">
                                                    <span className="date-field-label">اليوم</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="DD"
                                                        value={arrivalDate.day}
                                                        onChange={(e) => handleDateChange('day', e.target.value)}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                <span className="date-separator">-</span>
                                                <div className="date-field month-field">
                                                    <span className="date-field-label">الشهر</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="MM"
                                                        value={arrivalDate.month}
                                                        onChange={(e) => handleDateChange('month', e.target.value)}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                <span className="date-separator">-</span>
                                                <div className="date-field year-field">
                                                    <span className="date-field-label">السنة</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="YYYY"
                                                        value={arrivalDate.year}
                                                        onChange={(e) => handleDateChange('year', e.target.value)}
                                                        maxLength={4}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="calendar-trigger"
                                                    onClick={() => document.getElementById('edit-shipment-arrival-date-picker').showPicker()}
                                                >
                                                    <svg fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                <input
                                                    type="date"
                                                    id="edit-shipment-arrival-date-picker"
                                                    className="hidden-date-input"
                                                    value={formData.expected_arrival_date}
                                                    onChange={handleDatePickerChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: التكاليف والأسعار */}
                            <div className="form-section">
                                <h4 className="section-title">التكاليف والأسعار</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">
                                            تكلفة الشحنة
                                        </label>
                                        <input
                                            type="number"
                                            name="shipment_cost"
                                            className="form-input"
                                            value={formData.shipment_cost}
                                            onChange={handleInputChange}
                                            placeholder="أدخل تكلفة الشحنة (اختياري)"
                                            step="0.01"
                                            min="0"
                                            style={{
                                                borderColor: validationErrors.shipment_cost ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.shipment_cost ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            المجموع
                                            <span style={{ color: '#6c757d', marginRight: '8px', fontSize: '0.8rem', fontWeight: 'normal' }}>(الكمية × تكلفة الشحنة)</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="total_cost"
                                            className="form-input"
                                            value={formData.total_cost ? `${formData.total_cost}` : '-- يتم الحساب تلقائياً --'}
                                            readOnly
                                            style={{
                                                backgroundColor: '#f8f9fa',
                                                color: formData.total_cost ? '#03a9b3' : '#adb5bd',
                                                fontWeight: formData.total_cost ? '700' : '400',
                                                cursor: 'not-allowed',
                                                border: '2px solid #e1e8ed'
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            سعر البيع
                                        </label>
                                        <input
                                            type="number"
                                            name="selling_price"
                                            className="form-input"
                                            value={formData.selling_price}
                                            onChange={handleInputChange}
                                            placeholder="أدخل سعر البيع (اختياري)"
                                            step="0.01"
                                            min="0"
                                            style={{
                                                borderColor: validationErrors.selling_price ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.selling_price ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            العمولة
                                            <span style={{ color: '#6c757d', marginRight: '8px', fontSize: '0.8rem', fontWeight: 'normal' }}>(سعر البيع - تكلفة الشحن)</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="commission"
                                            className="form-input"
                                            value={formData.commission ? `${formData.commission}` : '-- يتم الحساب تلقائياً --'}
                                            readOnly
                                            style={{
                                                backgroundColor: '#f8f9fa',
                                                color: formData.commission ? (parseFloat(formData.commission) >= 0 ? '#28a745' : '#dc3545') : '#adb5bd',
                                                fontWeight: formData.commission ? '700' : '400',
                                                cursor: 'not-allowed',
                                                border: '2px solid #e1e8ed'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 5: ملاحظات */}
                            <div className="form-section">
                                <h4 className="section-title">ملاحظات</h4>
                                <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                                    <div className="form-group">
                                        <label className="form-label">ملاحظات إضافية</label>
                                        <textarea
                                            name="note"
                                            className="form-input"
                                            value={formData.note}
                                            onChange={handleInputChange}
                                            placeholder="أدخل أي ملاحظات إضافية (اختياري)"
                                            rows="3"
                                            style={{ resize: 'vertical', minHeight: '80px' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="error-message" style={{ marginTop: '1rem' }}>
                                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button 
                                    type="submit" 
                                    className="btn btn-accent btn-large"
                                    disabled={isLoading || isDeleting}
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
                                            <span>جاري الحفظ...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            <span>حفظ التعديلات</span>
                                        </>
                                    )}
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-outline btn-large"
                                    onClick={handleClose}
                                    disabled={isLoading || isDeleting}
                                >
                                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    <span>إلغاء</span>
                                </button>
                            </div>
                            <button 
                                type="button" 
                                className="btn btn-large"
                                onClick={handleDelete}
                                disabled={isLoading || isDeleting}
                                style={{
                                    backgroundColor: confirmDelete ? '#dc3545' : '#fff',
                                    color: confirmDelete ? '#fff' : '#dc3545',
                                    border: '2px solid #dc3545',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
                                        <span>جاري الحذف...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <span>{confirmDelete ? 'تأكيد الحذف' : 'حذف الشحنة'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Quick Add Modal */}
            <QuickAddModal
                isOpen={quickAddModal.isOpen}
                onClose={() => setQuickAddModal({ ...quickAddModal, isOpen: false })}
                onSave={handleQuickAddSave}
                title={quickAddModal.title}
                fieldLabel={quickAddModal.fieldLabel}
                fieldName="name"
                placeholder={`أدخل ${quickAddModal.fieldLabel}`}
                fields={quickAddModal.fields}
                editMode={quickAddModal.editMode}
                editData={quickAddModal.editData}
            />

            {/* Delete Confirmation Modal */}
            {deleteModal.isOpen && (
                <div className="modal-overlay" onClick={closeDeleteModal}>
                    <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header" style={{ background: deleteModal.hasError ? 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)' : 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' }}>
                            <h3 className="modal-title" style={{ color: 'white' }}>{deleteModal.hasError ? 'تنبيه' : 'تأكيد الحذف'}</h3>
                            <button type="button" className="modal-close" onClick={closeDeleteModal} style={{ color: 'white' }}>
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
                                {deleteModal.hasError ? (
                                    <p style={{ fontSize: '1.15rem', color: '#495057', fontWeight: '600', lineHeight: '1.8' }}>{error}</p>
                                ) : (
                                    <>
                                        <p style={{ fontSize: '1.1rem', color: '#2c3e50', marginBottom: '0.5rem', fontWeight: '600' }}>هل أنت متأكد من حذف</p>
                                        <p style={{ fontSize: '1.25rem', color: '#000', fontWeight: '700', marginBottom: '1rem' }}>"{deleteModal.item?.name}"</p>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            {deleteModal.hasError ? (
                                <button type="button" className="btn" onClick={closeDeleteModal} style={{ flex: 1, maxWidth: '250px', background: 'linear-gradient(135deg, #03428d 0%, #0066cc 100%)', color: 'white', border: 'none', padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600' }}>فهمت</button>
                            ) : (
                                <>
                                    <button type="button" className="btn" onClick={confirmDeleteItem} style={{ flex: 1, background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', color: 'white', border: 'none', padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600' }}>حذف</button>
                                    <button type="button" className="btn btn-outline" onClick={closeDeleteModal} style={{ flex: 1, background: 'white', color: '#6c757d', border: '2px solid #e1e8ed', padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600' }}>إلغاء</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EditShipmentModal;
